<template>
    <div class="CEO">
        <index
            :payload-for-index="payloadForIndex"
            @add="add"
            @view="view"
            @edit="edit"
            @remove="remove"
        />

        <create-or-view-or-edit
            ref="createOrEdit"
            v-model="isCEOSidebarActive"
            :data-status="status"
            :data-sidebar-action-loading="isSideBarActionLoading"
            :data-ceo="CEO"
            :data-auto-fill-requested-ceo="autoFillRequestedCEO"
            @add-ceo="create"
            @update-ceo="update"
            @request-autofill-ceo-data="requestAutofillCEOData"
        />

        <!-- delete -->
        <confirmation-modal  ref="modal">
            <template slot="body">
                <div>
                    <feather-icon class="d-block mx-auto text-warning" icon="AlertCircleIcon" size="90"/>
                    <h1 class="text-center text-secondary display-5 mt-2">Are you sure?</h1>
                    <p class="text-center mt-2"> You won't be able to revert this! </p>
                </div>
            </template>

            <template slot="footer">
                <b-button class="d-flex" :disabled="isSideBarActionLoading" size="md" variant="primary" @click="confirmDelete">
                    Yes, delete it!
                    <normal-loader v-if="isSideBarActionLoading" class="left-10%" />
                </b-button>

                <b-button size="md" variant="outline-danger" @click="cancelDelete">
                    Cancel
                </b-button>
            </template>
        </confirmation-modal>
    </div>
</template>

<script>
import {
    BButton,
} from "bootstrap-vue";
import { mapGetters } from "vuex";
import { ref } from '@vue/composition-api';
import { errorToast, successToast } from "@/views/helpers/toast";
import ConfirmationModal from '@/views/components/utils/ConfirmationModal.vue';
import CEO from '@/models/CEO';
import User from '@/models/User';
import { CEO_CREATE_SUCCESS, CEO_DELETE_SUCCESS, CEO_UPDATE_SUCCESS } from '@/constants/success';
import { generateRandomUniqueId } from '@/helpers';
import { REFRESH_AND_TRY_AGAIN_ERROR, SOMETHING_WENT_WRONG } from '@/constants/errors';
import useCEOTable from './useCEOTable';
import CreateOrViewOrEdit from './CreateOrViewOrEdit.vue';
import Index from './Index.vue';

export default {
    components: {
        Index,
        BButton,
        CreateOrViewOrEdit,
        ConfirmationModal,
    },

    data() {
        return {
            status: undefined,
            CEO: {},
            isCEOSidebarActive: false,
            isSideBarActionLoading: false,
            autoFillRequestedCEO: {}
        };
    },

    computed: {
        ...mapGetters({
            isUserAdmin: "auth/isAdmin",
        }),
    },

    mounted()
    {
        // just to make sure the menu bar is shown
        this.$store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', false);
    },

    methods:{
        async requestAutofillCEOData(id)
        {
            if (id) {
                const data = await User.getByNRIC(id);
                this.autoFillRequestedCEO = data;
            }
        },

        add(){
            this.CEO = {};
            this.status = "create";
            this.isCEOSidebarActive = true;
        },

        view(data){
            this.CEO = data.item;
            this.status = "view";
            this.isCEOSidebarActive = true;
        },

        edit(data){
            this.CEO = data.item;
            this.status = "edit";
            this.isCEOSidebarActive = true;
        }
    },

    setup() {
        const filterMode = ref(false);
        const dataToDelete = ref(undefined);
        // the list of these keys may not be same for even the same user(user_id)
        // the list will be ignored in frontend level update for the same user
        const IGNORE_KEYS_LIST = [
            "_id",
            "id",
            "appointmentDate",
            "cessationDate"
        ];

        const {
            // table data
            sortBy,
            perPage,
            isBusy,
            dataMeta,
            currentPage,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            perPageOptions,

            // UI
            isLoading,

            CEOs,
            refetchData,
            fetchCEOs,
            refCEOListTable,
            totalCEOs
        } = useCEOTable();

        const payloadForIndex = {
            sortBy,
            isBusy,
            perPage,
            dataMeta,
            CEOs,
            isLoading,
            currentPage,
            refetchData,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            perPageOptions,
            fetchCEOs,
            totalCEOs,
            refCEOListTable,
        };

        function create(ceo) {
            if (this.isUserAdmin)
            {
                this.isSideBarActionLoading = true;

                // eslint-disable-next-line no-param-reassign
                ceo._id = generateRandomUniqueId();
                if(!ceo.cessationDate) {
                    // eslint-disable-next-line no-param-reassign
                    ceo.cessationDate = null;
                }

                CEO.create(ceo).then(newDirector => {
                    this.isSideBarActionLoading = false;
                    this.isCEOSidebarActive = false;

                    refCEOListTable.value.localItems.push(ceo);
                    
                    // set the id from server to the new ceo
                    refCEOListTable.value.localItems.forEach(item => {
                        if(item._id === ceo._id) {
                            // eslint-disable-next-line no-param-reassign
                            item.id = newDirector.id;
                            // eslint-disable-next-line no-param-reassign
                            item.userId = newDirector.user_id;
                        }
                    });
                    successToast(CEO_CREATE_SUCCESS);
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to create ceo",
                            e
                        );
                    });
            }
        };

        function update(data) {
            if (this.isUserAdmin)
            {
                this.isSideBarActionLoading = true;

                CEO.update(data.id, data).then(() => {
                    this.isSideBarActionLoading = false;
                    this.isCEOSidebarActive = false;

                    refCEOListTable.value.localItems.forEach(item => {
                        if(item._id === data._id) {
                            Object.keys(data).forEach(key => {
                            // eslint-disable-next-line no-param-reassign
                                item[key] = data[key];
                            });
                        }

                        // update incase of same user id too
                        if(item.userId === data.userId) {
                            Object.keys(data).forEach(key => {
                                // update user related keys
                                if (! IGNORE_KEYS_LIST.includes(key))
                                {
                                    // eslint-disable-next-line no-param-reassign
                                    item[key] = data[key];
                                }
                            });
                        }
                    });
                    this.CEO = {};

                    successToast(CEO_UPDATE_SUCCESS);
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to update CEO",
                            e
                        );
                    });
            }
        };

        function remove(data) {
            this.$refs.modal.$refs.confirmationModal.show();
            dataToDelete.value= data;
        };

        function cancelDelete() {
            dataToDelete.value = null;
            this.$refs.modal.$refs.confirmationModal.hide();
        }

        function confirmDelete() {
            if (this.isUserAdmin) {
                this.isSideBarActionLoading = true;

                if (! dataToDelete.value.item.id)
                {
                    errorToast(REFRESH_AND_TRY_AGAIN_ERROR);
                    return;
                }

                CEO.delete(dataToDelete.value.item.userId).then(() => {
                    refCEOListTable.value.localItems = refCEOListTable.value.localItems.filter(item => dataToDelete.value.item._id !== item._id);
                    this.$refs.modal.$refs.confirmationModal.hide();

                    this.isSideBarActionLoading = false;
                    dataToDelete.value = null;
                    successToast(CEO_DELETE_SUCCESS);
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to delete CEO",
                            e
                        );
                    });
            }
        };

        return {
            refCEOListTable,
            refetchData,

            // UI
            filterMode,

            // methods
            create,
            update,
            remove,
            confirmDelete,
            cancelDelete,
            payloadForIndex
        };
    }
};
</script>
